package co.uk.paxes.pinpoint.web

import kotlinx.browser.window
import kotlin.js.Date

/**
 * PlayGama-compatible storage manager.
 * 
 * Layered persistence strategy:
 * 1. PlayGama: bridge.storage (cloud saves with player data sync)
 * 2. Android/Web: localStorage (local persistence)
 * 
 * ✅ Uses bridge.storage (per PlayGama docs) instead of window.platform.storage
 * ✅ Safe: Keeps localStorage fallback for Android and web versions
 * ✅ Compatible: Same API for SettingsManager, other callers
 */
object PlayGamaStorageManager {
    
    private var isBridgeAvailable = false
    private var isBridgeInitialized = false
    private var bridgeStorageAvailable = false
    private var bridgeAvailabilityChecked = false
    private var onStorageReadyCallback: (() -> Unit)? = null
    
    fun setBridgeInitialized() {
        isBridgeInitialized = true
        recheckStorageAvailability()
    }
    
    // Check storage availability
    private fun checkBridgeAvailability() {
        try {
            val bridge: dynamic = js("window.bridge")
            isBridgeAvailable = bridge != null
            val isInit = isBridgeInitialized || (bridge != null && bridge.isInitialized == true)
            if (isBridgeAvailable && isInit) {
                bridgeStorageAvailable = bridge.storage != null
            } else {
                bridgeStorageAvailable = false
            }
        } catch (e: Exception) {
            isBridgeAvailable = false
            bridgeStorageAvailable = false
        }
        
        if (!bridgeAvailabilityChecked) {
            logStorageStatus()
            bridgeAvailabilityChecked = true
        }
        
        // If storage just became available, trigger callback
        if (bridgeStorageAvailable && onStorageReadyCallback != null) {
            console.log("🔄 Storage ready, triggering callback")
            val cb = onStorageReadyCallback
            onStorageReadyCallback = null
            cb?.invoke()
        }
    }
    
    /**
     * Get an item from storage.
     * For PlayGama: Keep undo credits in a special cache that survives clears
     * For web browser: Use localStorage only
     */
    fun getItem(key: String): String? {
        checkBridgeAvailability()
        return try {
            // For undo credits on PlayGama: use in-memory cache as primary source
            if (key == "pinpoint_undo_credits" && bridgeStorageAvailable) {
                // Check in-memory cache first
                val cached = window.asDynamic().pinpointUndoCreditCache
                if (cached != null && cached != "undefined") {
                    console.log("📥 Retrieved undo credits from memory cache: $cached")
                    return cached as String
                }
                
                // Fallback to localStorage
                val value = window.localStorage.getItem(key)
                if (value != null) {
                    // Update memory cache
                    window.asDynamic().pinpointUndoCreditCache = value
                    console.log("📥 Retrieved undo credits from localStorage and cached: $value")
                    return value
                }
                
                console.warn("⚠️ Undo credits not found anywhere")
                return null
            }
            
            // For web browser (no bridge): just use localStorage
            if (!bridgeStorageAvailable) {
                val value = window.localStorage.getItem(key)
                if (value != null) {
                    console.log("📥 Retrieved from localStorage: $key = '$value'")
                }
                return value
            }
            
            // For other PlayGama keys: Try bridge first, then localStorage
            val cached = tryGetFromBridgeStorage(key)
            if (cached != null) {
                console.log("📥 Retrieved from bridge.storage: $key")
                return cached
            }
            
            // Fallback to localStorage
            val value = window.localStorage.getItem(key)
            if (value != null) {
                console.log("📥 Retrieved from localStorage: $key")
            }
            value
        } catch (e: Exception) {
            console.warn("⚠️ Storage retrieval failed for $key: ${e.message}")
            null
        }
    }
    
    /**
     * Set an item in storage.
     * Saves to bridge.storage (PlayGama) AND localStorage (backup).
     * For undo credits: also updates in-memory cache.
     */
    fun setItem(key: String, value: String) {
        checkBridgeAvailability()
        try {
            // For undo credits on PlayGama: update memory cache too
            if (key == "pinpoint_undo_credits" && bridgeStorageAvailable) {
                window.asDynamic().pinpointUndoCreditCache = value
                console.log("💾 Updated memory cache for undo credits: $value")
            }
            
            // Try bridge.storage first (PlayGama - async, non-blocking)
            if (bridgeStorageAvailable) {
                trySetBridgeStorage(key, value)
            }
            
            // Always also save to localStorage (Android, web, backup for PlayGama)
            window.localStorage.setItem(key, value)
            console.log("📤 Saved to localStorage: $key")
        } catch (e: Exception) {
            console.error("❌ Storage save failed for $key: ${e.message}")
        }
    }
    
    /**
     * Remove an item from storage.
     */
    fun removeItem(key: String) {
        checkBridgeAvailability()
        try {
            if (bridgeStorageAvailable) {
                tryRemoveBridgeStorage(key)
            }
            
            window.localStorage.removeItem(key)
            console.log("🗑️ Removed from localStorage: $key")
        } catch (e: Exception) {
            console.warn("⚠️ Storage remove failed for $key: ${e.message}")
        }
    }
    
    /**
     * Clear all stored items.
     */
    fun clear() {
        checkBridgeAvailability()
        try {
            if (bridgeStorageAvailable) {
                tryClearBridgeStorage()
            }
            
            window.localStorage.clear()
            console.log("🗑️ Cleared localStorage")
        } catch (e: Exception) {
            console.warn("⚠️ Storage clear failed: ${e.message}")
        }
    }
    
    /**
     * Try to get item from bridge.storage (PlayGama).
     * Returns null if not available or fails.
     */
    private fun tryGetFromBridgeStorage(key: String): String? {
        return try {
            if (!bridgeStorageAvailable) return null
            
            val bridge: dynamic = js("window.bridge")
            val storage = bridge?.storage
            
            // bridge.storage.get([keys]) returns promise
            // For sync API, we check if value is already in localStorage as backup
            null  // Fallback to localStorage for sync calls
        } catch (e: Exception) {
            console.warn("⚠️ Bridge storage get failed: ${e.message}")
            null
        }
    }
    
    /**
     * Try to set item in bridge.storage (PlayGama).
     */
    private fun trySetBridgeStorage(key: String, value: String) {
        try {
            if (!bridgeStorageAvailable) return
            
            val bridge: dynamic = js("window.bridge")
            val storage = bridge?.storage
            
            if (storage != null && js("typeof storage.set === 'function'")) {
                val promise: dynamic = storage.set(arrayOf(key), arrayOf(value))
                
                if (promise != null) {
                    promise.then({
                        console.log("📤 bridge.storage saved: $key")
                    }, { err: dynamic ->
                        console.warn("⚠️ bridge.storage save failed: $err (using localStorage backup)")
                    })
                }
            }
        } catch (e: Exception) {
            console.warn("⚠️ Exception in trySetBridgeStorage: ${e.message}")
        }
    }
    
    /**
     * Try to remove item from bridge.storage (PlayGama).
     */
    private fun tryRemoveBridgeStorage(key: String) {
        try {
            if (!bridgeStorageAvailable) return
            
            val bridge: dynamic = js("window.bridge")
            val storage = bridge?.storage
            
            if (storage != null && js("typeof storage.delete === 'function'")) {
                val promise: dynamic = storage.delete(arrayOf(key))
                
                if (promise != null) {
                    promise.then({
                        console.log("🗑️ bridge.storage removed: $key")
                    }, { err: dynamic ->
                        console.warn("⚠️ bridge.storage remove failed: $err")
                    })
                }
            }
        } catch (e: Exception) {
            console.warn("⚠️ Exception in tryRemoveBridgeStorage: ${e.message}")
        }
    }
    
    /**
     * Try to clear bridge.storage (PlayGama).
     */
    private fun tryClearBridgeStorage() {
        try {
            if (!bridgeStorageAvailable) return
            
            val bridge: dynamic = js("window.bridge")
            val storage = bridge?.storage
            
            if (storage != null && js("typeof storage.delete === 'function'")) {
                console.log("ℹ️ bridge.storage clear skipped (use localStorage for backup)")
            }
        } catch (e: Exception) {
            console.warn("⚠️ Exception in tryClearBridgeStorage: ${e.message}")
        }
    }
    
    /**
     * Log storage status on startup.
     */
    private fun logStorageStatus() {
        if (bridgeStorageAvailable) {
            console.log("✅ PlayGama bridge.storage available - using for cloud saves")
        } else if (isBridgeAvailable) {
            console.log("⚠️ PlayGama bridge available but storage not ready - using localStorage")
        } else {
            console.log("ℹ️ PlayGama bridge not available (Android/web/web-link mode) - using localStorage")
        }
    }
    
    // ========================================
    // PlayGama Save/Restore Methods
    // ========================================
    
    private const val GAME_STATE_KEY = "pinpoint_local_game_state"
    private const val MAX_AGE_MS = 24 * 60 * 60 * 1000L  // 24 hours
    
    /**
     * Check if storage is available for save/restore (always true via localStorage/bridge)
     */
    fun isSaveRestoreAvailable(): Boolean {
        checkBridgeAvailability()
        return true
    }
    
    /**
     * Re-check storage availability after bridge is initialized.
     */
    fun recheckStorageAvailability() {
        bridgeAvailabilityChecked = false
        checkBridgeAvailability()
    }
    
    /**
     * Register a callback to be called when storage becomes available.
     */
    fun onStorageReady(callback: () -> Unit) {
        checkBridgeAvailability()
        if (bridgeStorageAvailable) {
            callback()
        } else {
            onStorageReadyCallback = callback
        }
    }
    
    /**
     * Save game state to storage (PlayGama cloud + localStorage)
     */
    fun saveGameState(stateJson: String, callback: ((Boolean) -> Unit)? = null) {
        try {
            // ALWAYS save to localStorage (this is our primary instant source for restore)
            window.localStorage.setItem(GAME_STATE_KEY, stateJson)
            console.log("💾 Saved to localStorage (primary source)")
            
            checkBridgeAvailability()
            if (!bridgeStorageAvailable) {
                console.log("ℹ️ PlayGama bridge.storage not available - using localStorage only")
                callback?.invoke(true)
                return
            }
            
            // Also try to save to PlayGama storage as cloud backup
            console.log("💾 Attempting to save to PlayGama storage as backup...")
            val bridge: dynamic = js("window.bridge")
            val storage = bridge?.storage
            
            if (storage != null && js("typeof storage.set === 'function'")) {
                val promise: dynamic = storage.set(arrayOf(GAME_STATE_KEY), arrayOf(stateJson))
                
                if (promise != null && js("typeof promise.then === 'function'")) {
                    promise.then({
                        console.log("✅ Game state also saved to bridge.storage (backup)")
                        callback?.invoke(true)
                    }, { err: dynamic ->
                        console.warn("⚠️ Failed to save to bridge.storage: $err (localStorage is still OK)")
                        callback?.invoke(true)  // Still success since localStorage worked
                    })
                } else {
                    callback?.invoke(true)
                }
            } else {
                console.log("ℹ️ bridge.storage.set not available - localStorage only")
                callback?.invoke(true)
            }
        } catch (e: Exception) {
            console.error("❌ Exception saving game state: ${e.message}")
            callback?.invoke(false)
        }
    }
    
    /**
     * Load game state from PlayGama storage or localStorage
     */
    fun loadGameState(callback: (String?) -> Unit) {
        checkBridgeAvailability()
        if (!bridgeStorageAvailable) {
            console.log("ℹ️ PlayGama storage not available - checking localStorage")
            val value = window.localStorage.getItem(GAME_STATE_KEY)
            if (value != null && value.isNotEmpty()) {
                console.log("📥 Loaded from localStorage")
                callback(value)
            } else {
                callback(null)
            }
            return
        }
        
        try {
            console.log("📥 Loading game state from PlayGama storage...")
            val bridge: dynamic = js("window.bridge")
            val storage = bridge?.storage
            
            if (storage != null && js("typeof storage.get === 'function'")) {
                val promise: dynamic = storage.get(arrayOf(GAME_STATE_KEY))
                
                if (promise != null && js("typeof promise.then === 'function'")) {
                    promise.then({ result: dynamic ->
                        val json = parseStorageResult(result)
                        
                        if (json != null && json.isNotEmpty()) {
                            console.log("✅ Game state loaded successfully from bridge.storage")
                            callback(json)
                        } else {
                            // Fallback to localStorage if bridge storage didn't return data
                            console.log("ℹ️ bridge.storage returned no data, checking localStorage")
                            val localStorageValue = window.localStorage.getItem(GAME_STATE_KEY)
                            if (localStorageValue != null && localStorageValue.isNotEmpty()) {
                                console.log("📥 Loaded from localStorage")
                                callback(localStorageValue)
                            } else {
                                console.log("ℹ️ No saved game found")
                                callback(null)
                            }
                        }
                    }, { err: dynamic ->
                        console.error("❌ Failed to load game state from bridge.storage: $err")
                        val localStorageValue = window.localStorage.getItem(GAME_STATE_KEY)
                        if (localStorageValue != null && localStorageValue.isNotEmpty()) {
                            console.log("📥 Loaded from localStorage after error")
                            callback(localStorageValue)
                        } else {
                            callback(null)
                        }
                    })
                } else {
                    val localStorageValue = window.localStorage.getItem(GAME_STATE_KEY)
                    callback(localStorageValue)
                }
            } else {
                val localStorageValue = window.localStorage.getItem(GAME_STATE_KEY)
                callback(localStorageValue)
            }
        } catch (e: Exception) {
            console.error("❌ Exception loading game state: ${e.message}")
            val localStorageValue = window.localStorage.getItem(GAME_STATE_KEY)
            callback(localStorageValue)
        }
    }
    
    /**
     * Parse the storage result from bridge.storage.get()
     */
    private fun parseStorageResult(result: dynamic): String? {
        if (result == null) return null
        
        // Check if result is an array
        if (js("Array.isArray(result)")) {
            val arr: dynamic = result
            if (arr.length > 0) {
                val firstValue = arr[0]
                if (js("typeof firstValue === 'string'") && firstValue != "") {
                    return firstValue
                }
                if (js("typeof firstValue === 'object' && firstValue !== null")) {
                    return JSON.stringify(firstValue)
                }
            }
        }
        
        // If result is an object with our key
        if (js("typeof result === 'object'")) {
            val value = result[GAME_STATE_KEY]
            if (js("typeof value === 'string'") && value != "") {
                return value
            }
            if (js("typeof value === 'object' && value !== null")) {
                return JSON.stringify(value)
            }
        }
        
        return null
    }
    
    /**
     * Clear saved game state (async)
     */
    fun clearSavedGame(callback: ((Boolean) -> Unit)? = null) {
        try {
            // Always clear localStorage
            window.localStorage.removeItem(GAME_STATE_KEY)
            console.log("🗑️ Cleared from localStorage")
            
            checkBridgeAvailability()
            if (!bridgeStorageAvailable) {
                callback?.invoke(true)
                return
            }
            
            val bridge: dynamic = js("window.bridge")
            val storage = bridge?.storage
            
            if (storage != null && js("typeof storage.delete === 'function'")) {
                val promise: dynamic = storage.delete(arrayOf(GAME_STATE_KEY))
                
                if (promise != null && js("typeof promise.then === 'function'")) {
                    promise.then({
                        console.log("🗑️ Saved game cleared from bridge.storage")
                        callback?.invoke(true)
                    }, { err: dynamic ->
                        console.error("❌ Failed to clear saved game: $err")
                        callback?.invoke(false)
                    })
                } else {
                    callback?.invoke(true)
                }
            } else {
                callback?.invoke(true)
            }
        } catch (e: Exception) {
            console.error("❌ Exception clearing saved game: ${e.message}")
            callback?.invoke(false)
        }
    }
    
    /**
     * Check if a saved game exists
     */
    fun hasSavedGame(callback: (Boolean) -> Unit) {
        loadGameState { json ->
            if (json != null && json.isNotEmpty()) {
                val state = GameSaveState.fromJson(json)
                if (state != null) {
                    val age = Date.now().toLong() - state.lastSaveTimestamp
                    if (age > MAX_AGE_MS) {
                        console.log("⏰ Saved game too old (${age / 1000 / 60 / 60} hours), clearing")
                        clearSavedGame {
                            callback(false)
                        }
                        return@loadGameState
                    }
                    callback(true)
                } else {
                    callback(false)
                }
            } else {
                callback(false)
            }
        }
    }
}
